import { z } from 'zod';
import { docSuggestion } from './ai.js';
export const documentEntity = z.object({
    id: z.number().int().positive(),
    projectId: z.number().int().nullable().optional(),
    customerId: z.number().int().nullable().optional(),
    filename: z.string(),
    fileSize: z.number().int().nonnegative(),
    mimeType: z.string(),
    checksum: z.string(),
    aiConfidence: z.number().int().nullable().optional(),
    aiSuggestions: z.array(docSuggestion).nullable().optional(),
    assignedBy: z.enum(['ai', 'manual']).nullable().optional(),
    uploadDate: z.string().datetime(),
    notes: z.string().nullable().optional(),
    /**
     * Convenience-Feld vom Backend: Titel des zugeordneten Projekts.
     * Nur befüllt, wenn projectId gesetzt ist — sonst null/undefined.
     * Vermeidet N+1-Lookup im Frontend bei "Alle Dokumente"-Listen.
     */
    projectTitle: z.string().nullable().optional(),
    /**
     * Objektbetreuung: Zuordnung zu einer Liegenschaft und zu einem Typ aus dem
     * Dokumentkatalog. Beides unabhängig von Projekt und Kunde — ein
     * Wartungsnachweis gehört zum Objekt, nicht zu einem Bauvorhaben.
     */
    liegenschaftId: z.number().int().nullable().optional(),
    dokumentTyp: z.string().nullable().optional(),
    /** Convenience-Feld vom Backend: Bezeichnung der zugeordneten Liegenschaft. */
    liegenschaftBezeichnung: z.string().nullable().optional(),
});
/**
 * Ist ein Dokument zugeordnet? An ein Projekt ODER an ein betreutes Objekt.
 *
 * EINZIGE Definition — Inbox-Filter, Dashboard-Zähler und die Zeilen im
 * Eingang müssen dieselbe Menge meinen, sonst zeigt die Zeile „Objekt öffnen"
 * und der Zähler daneben sagt „wartet auf Zuordnung".
 */
export function isDocumentAssigned(doc) {
    return doc.projectId != null || doc.liegenschaftId != null;
}
/**
 * Zielangaben beim Upload (`POST /documents?liegenschaftId=…&dokumentTyp=…`).
 *
 * Als Query-String, nicht als Multipart-Felder: busboy liefert nur die
 * Felder, die VOR dem Datei-Part stehen — und die Reihenfolge bestimmt der
 * Client. Ein Vertrag, der von der Feldreihenfolge abhängt, ist keiner.
 */
export const uploadZielFelder = z.object({
    liegenschaftId: z.coerce.number().int().positive().optional(),
    /** Schlüssel aus `dokumentkatalog.json`; unbekannte Schlüssel lehnt das Backend ab. */
    dokumentTyp: z.string().trim().min(1).max(80).optional(),
});
/**
 * PATCH /documents/:id — Partial-Update-Semantik (nullish):
 *   Feld fehlt   → bleibt unverändert
 *   Feld = null  → bewusstes Entfernen (Zuordnung lösen / Notiz löschen)
 */
export const assignDocumentRequest = z.object({
    projectId: z.number().int().positive().nullable().optional(),
    customerId: z.number().int().positive().nullable().optional(),
    notes: z.string().nullable().optional(),
    liegenschaftId: z.number().int().positive().nullable().optional(),
    /** Schlüssel aus `dokumentkatalog.json`; unbekannte Schlüssel lehnt das Backend ab. */
    dokumentTyp: z.string().trim().max(80).nullable().optional(),
});
// ---- Dateityp-Klassifizierung ---------------------------------------------
/**
 * Grobe Dateityp-Klassen für Filter und Symbolik. Bewusst fachlich statt
 * technisch: der Planer denkt in „Plan (PDF)", „Foto", „Word-Datei",
 * „E-Mail" — nicht in MIME-Types. `other` fängt alles Übrige (CAD, Audio,
 * Video, Notizen), damit ein Filter nie Dokumente unsichtbar macht, ohne
 * dass es dafür eine Kachel gäbe.
 */
export const documentFileType = z.enum(['pdf', 'image', 'word', 'email', 'other']);
/**
 * MIME-Zuordnung je Klasse — EINZIGE Quelle für Backend-Filter (WHERE) und
 * Frontend-Anzeige. Prefix-Regeln decken ganze Familien ab (`image/`),
 * `exact` die Einzelfälle, für die es keinen gemeinsamen Prefix gibt.
 */
export const DOCUMENT_FILE_TYPE_MIME = {
    pdf: { exact: ['application/pdf'], prefixes: [] },
    image: { exact: [], prefixes: ['image/'] },
    word: {
        exact: [
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ],
        prefixes: [],
    },
    email: {
        exact: ['message/rfc822', 'application/vnd.ms-outlook', 'application/x-msg'],
        prefixes: [],
    },
};
/** Ordnet einen MIME-Type genau einer Klasse zu; unbekannt → `other`. */
export function fileTypeFromMime(mimeType) {
    const mime = mimeType.toLowerCase();
    for (const [type, rule] of Object.entries(DOCUMENT_FILE_TYPE_MIME)) {
        if (rule.exact.includes(mime))
            return type;
        if (rule.prefixes.some((p) => mime.startsWith(p)))
            return type;
    }
    return 'other';
}
/** Deutsche Labels für die Filter-Auswahl. */
export const DOCUMENT_FILE_TYPE_LABEL = {
    pdf: 'PDF',
    image: 'Bild',
    word: 'Word',
    email: 'E-Mail',
    other: 'Sonstige',
};
// ---- GET /documents — Query-Contract --------------------------------------
/**
 * Zuordnungsstatus-Filter der Dokumentliste.
 * `all` ist der Default und wird bewusst mitgeführt (statt „Parameter fehlt"),
 * damit der Zustand des UI-Filters eins zu eins in der URL steht.
 */
export const documentAssignmentFilter = z.enum(['all', 'assigned', 'unassigned']);
/**
 * Query-Contract von `GET /documents`.
 *
 * `q` durchsucht ausschließlich den **Dateinamen** (Teilwort, Groß-/
 * Kleinschreibung egal, mehrere Wörter werden UND-verknüpft). Dokument-
 * INHALTE sind bewusst nicht durchsuchbar: `Document` hat kein Textfeld,
 * es wird beim Upload kein Text extrahiert und gespeichert. Ein „Inhalts-
 * Filter", der in Wahrheit nur Dateinamen liest, wäre eine Lüge im UI.
 */
export const documentListQuery = z.object({
    projectId: z.coerce.number().int().positive().optional(),
    customerId: z.coerce.number().int().positive().optional(),
    /** Objektbetreuung: nur Dokumente dieser Liegenschaft. */
    liegenschaftId: z.coerce.number().int().positive().optional(),
    /** Wenn true zusammen mit projectId: auch Dokumente ohne projectId zurückliefern. */
    includeUnassigned: z.coerce.boolean().optional(),
    /**
     * Legacy-Kurzform für `assignment=unassigned` (Inbox-Pickers nutzen sie).
     * Bleibt erhalten, damit bestehende Aufrufer nicht brechen.
     */
    unassigned: z.coerce.boolean().optional(),
    /** Volltextsuche über den Dateinamen. Leerstring = kein Filter. */
    q: z.string().trim().max(200).optional(),
    assignment: documentAssignmentFilter.default('all'),
    /** Mehrfachauswahl wäre hier Overkill — ein Typ oder keiner. */
    fileType: documentFileType.optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
});
export const documentListResponse = z.object({
    items: z.array(documentEntity),
    total: z.number().int().min(0),
    limit: z.number().int().positive(),
    offset: z.number().int().min(0),
});
